import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { format } from 'date-fns';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import { parseTutarTR } from '../../utils/para';
import { useToast } from '../../components/Toast';
import { logError } from '../../utils/logError';
import useUrlState from '../../hooks/useUrlState';
import SearchableSelect from '../../components/SearchableSelect';
import EnterNavForm from '../../components/EnterNavForm';
import { ciftGirisOnayMetni } from '../../utils/ciftGiris';
import { donusumOnizleme } from '../../utils/birimDonusum';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BIRIM_OPTIONS = ['KG', 'ADET', 'LT', 'PAKET', 'KOLI', 'PORSIYON'];

// Sayıyı TR ondalık (virgül) string'e çevir, gereksiz sıfırları at. 156.25→"156,25".
const trSayi = (n, d = 2) =>
  Number(n || 0).toFixed(d).replace(/\.?0+$/, '').replace('.', ',');

// ==================== AUTOCOMPLETE COMPONENT ====================
// Öneri listesi PORTAL ile document.body'ye fixed pozisyonda render edilir —
// böylece Kalemler tablosunun overflowX:auto sarmalayıcısı dropdown'u kırpamaz.
// Klavye navigasyonu (yön tuşları + Enter + Escape) eklendi.
const MalzemeAutocomplete = ({ malzemeler, value, onChange, onSelect }) => {
  const [query, setQuery] = useState('');
  const [showList, setShowList] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [coords, setCoords] = useState(null); // {top,left,width} | null
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Secili malzeme adini goster
  useEffect(() => {
    if (value) {
      const m = malzemeler.find(mal => String(mal.id) === String(value));
      if (m) setQuery(m.ad);
    } else {
      setQuery('');
    }
  }, [value, malzemeler]);

  // filtered degisince / liste kapaninca aktif satiri sifirla
  useEffect(() => {
    setActiveIndex(-1);
  }, [filtered]);

  // Portal dropdown'un input'a göre konumunu hesapla (fixed pozisyon).
  // Liste açıkken scroll (capture=true → overflowX:auto sarmalayıcının
  // scroll'unu da yakalar) ve resize'da yeniden hesapla.
  useEffect(() => {
    if (!showList) {
      setCoords(null);
      return;
    }
    const recompute = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom, left: r.left, width: r.width });
    };
    recompute();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [showList]);

  // Disari tiklaninca kapat — portal wrapperRef dışında olduğu için
  // hem wrapperRef hem listRef dışındaysa kapat.
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inWrapper && !inList) {
        setShowList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(''); // seçimi temizle

    if (val.length >= 1) {
      const lower = normalizeForSearch(val);
      const results = malzemeler.filter(m =>
        searchMatchPre(m.ad, lower) ||
        (m.kod && searchMatchPre(m.kod, lower))
      );
      setFiltered(results.slice(0, 15));
      setShowList(true);
    } else {
      setFiltered([]);
      setShowList(false);
    }
  };

  const handleSelect = (m) => {
    setQuery(m.ad);
    setShowList(false);
    onSelect(m);
  };

  const handleFocus = () => {
    if (!value && query.length >= 1) {
      setShowList(true);
    } else if (!value) {
      // Bos iken tum listeyi goster
      setFiltered(malzemeler.slice(0, 15));
      setShowList(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showList && filtered.length > 0) setShowList(true);
      setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (showList && filtered.length > 0) {
        e.preventDefault();
        handleSelect(filtered[activeIndex >= 0 ? activeIndex : 0]);
      }
    } else if (e.key === 'Escape') {
      setShowList(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder="Malzeme ara..."
        style={{
          width: '100%',
          padding: '8px 12px',
          border: `1px solid ${colors.border}`,
          borderRadius: radius.sm,
          fontSize: '14px',
          boxSizing: 'border-box',
          background: value ? '#f0fff0' : 'white'
        }}
      />
      {showList && filtered.length > 0 && coords && ReactDOM.createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: coords.width,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '0 0 6px 6px',
            boxShadow: shadows.md,
            maxHeight: '220px',
            overflowY: 'auto',
            zIndex: 12000
          }}
        >
          {filtered.map((m, idx) => (
            <div
              key={m.id}
              onMouseDown={() => handleSelect(m)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '13px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: idx === activeIndex ? colors.infoLight : 'white'
              }}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span><strong>{m.ad}</strong></span>
              <span style={{ color: colors.textTertiary, fontSize: '11px' }}>
                {m.birim} {m.fiyat > 0 ? `| ${m.fiyat.toFixed(2)} TL` : ''}
              </span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ==================== TEDARIKCI EKLEME MODALI ====================
const TedarikçiEkleModal = ({ onClose, onSaved }) => {
  const [unvan, setUnvan] = useState('');
  const [kategori, setKategori] = useState('');
  const [yetkili, setYetkili] = useState('');
  const [telefon, setTelefon] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!unvan.trim()) {
      setError('Unvan zorunludur');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/tedarikciler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          unvan: unvan.trim(),
          kategori: kategori || null,
          yetkili_kisi: yetkili || null,
          telefon: telefon || null
        })
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Kayıt hatası');
      }
      const newTedarikci = await res.json();
      onSaved(newTedarikci);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999
    }} onClick={onClose}>
      <div style={{
        background: colors.surface, borderRadius: radius.lg, padding: '24px',
        width: '420px', maxWidth: '92vw', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: styles.secondary }}>Yeni Tedarikçi Ekle</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '20px',
            cursor: 'pointer', color: colors.textTertiary, padding: '4px'
          }}>X</button>
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', marginBottom: '12px', borderRadius: radius.sm,
            background: '#f8d7da', color: '#721c24', fontSize: '13px'
          }}>{error}</div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <label style={modalLabelStyle}>Unvan *</label>
          <input value={unvan} onChange={e => setUnvan(e.target.value)}
            placeholder="Firma unvani" style={modalInputStyle} autoFocus />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={modalLabelStyle}>Kategori</label>
          <input value={kategori} onChange={e => setKategori(e.target.value)}
            placeholder="Orn: Et, Sebze, Ambalaj..." style={modalInputStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={modalLabelStyle}>Yetkili Kisi</label>
            <input value={yetkili} onChange={e => setYetkili(e.target.value)}
              placeholder="Ad Soyad" style={modalInputStyle} />
          </div>
          <div>
            <label style={modalLabelStyle}>Telefon</label>
            <input value={telefon} onChange={e => setTelefon(e.target.value)}
              placeholder="0xxx xxx xx xx" style={modalInputStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            background: colors.backgroundAlt, color: '#333', border: `1px solid ${colors.border}`,
            borderRadius: radius.sm, padding: '8px 20px', cursor: 'pointer', fontSize: '14px'
          }}>İptal</button>
          <button onClick={handleSave} disabled={saving} style={{
            background: saving ? '#ccc' : styles.primary, color: 'white',
            border: 'none', borderRadius: radius.sm, padding: '8px 20px',
            cursor: saving ? 'default' : 'pointer', fontSize: '14px', fontWeight: 'bold'
          }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
        </div>
      </div>
    </div>
  );
};

const modalLabelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold', color: '#555' };
const modalInputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box'
};

// ==================== TOGGLE COMPONENT ====================
const Toggle = ({ checked, onChange, label, activeColor = colors.warning }) => {
  const [focused, setFocused] = React.useState(false);
  return (
    <label style={{ display:'inline-flex', alignItems:'center', gap:'10px', cursor:'pointer', fontSize:'14px', fontWeight:'bold', color: colors.textPrimary }}>
      <span role="switch" aria-checked={checked} style={{ position:'relative', width:'34px', height:'20px', borderRadius:radius.full, background: checked ? activeColor : '#cbd5e1', transition:'background .15s', flexShrink:0, boxShadow: focused ? shadows.glow : 'none' }}>
        <span style={{ position:'absolute', top:'2px', left: checked ? '16px':'2px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left .15s' }} />
      </span>
      <input type="checkbox" checked={checked} onChange={onChange} onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)} style={{ position:'absolute', opacity:0, width:0, height:0 }} />
      {label}
    </label>
  );
};

// Tarih gösterimi — ISO tarihi dd.MM.yyyy biçimine çevir, geçersizse ham değeri döndür
const formatTarihGoster = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return format(d, 'dd.MM.yyyy');
};

// ==================== ANA SAYFA ====================
const FiyatGirisiPage = () => {
  useAuth(); // auth check
  const toast = useToast();
  const [tedarikciler, setTedarikçiler] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [selectedTedarikci, setSelectedTedarikci] = useState('');
  const [selectedFirma, setSelectedFirma] = useState('TORUNA_KASAP');
  const [selectedLokasyon, setSelectedLokasyon] = useState('');
  const [fiyatTarihi, setFiyatTarihi] = useState(new Date().toISOString().split('T')[0]);
  const [açıklama, setAçıklama] = useState('');
  const [kdvDahil, setKdvDahil] = useState(false);
  const [kdvOrani, setKdvOrani] = useState(10);
  const [kalemler, setKalemler] = useState([
    { malzeme_id: '', miktar: '', birim_fiyat: '', birim: 'KG', kdv_orani: null }
  ]);
  const [loading, setLoading] = useState(false);
  // Çapraz-kanal çift giriş onayı: backend onay_gerekli dönerse çakışmaları tutar,
  // kullanıcı "Yine de kaydet" derse aynı payload onayla:true ile yeniden gönderilir.
  const [ciftGirisCakismalar, setCiftGirisCakismalar] = useState(null);
  const [gecmis, setGecmis] = useState([]);
  const [showGecmis, setShowGecmis] = useState(false);
  const [gecmisLoading, setGecmisLoading] = useState(false);
  const [showTedarikciModal, setShowTedarikciModal] = useState(false);
  const [showMalzemeModal, setShowMalzemeModal] = useState(false);
  const [yeniMalzemeForm, setYeniMalzemeForm] = useState({ kod: '', ad: '', kategori: 'Hammadde', birim: 'KG', min_stok: '0', marka_id: '' });
  const [markalar, setMarkalar] = useState([]);
  const [yeniMalzemeSaving, setYeniMalzemeSaving] = useState(false);
  // Fatura bazlı düzenleme modalı (eski per-satır düzenlemenin yerine geçti).
  // openFaturaDuzenle ile doldurulur; kalemler ham string olarak tutulur (virgül girişi).
  const [faturaDuzenle, setFaturaDuzenle] = useState(null);
  const [faturaDuzenleSaving, setFaturaDuzenleSaving] = useState(false);
  // Malzeme bazlı BirimDonusum cache (malzeme_id → BD array). Fiyat girişi sırasında
  // kullanıcı doğru alış birimini seçebilsin diye ve payload'da giris_birimi gönderelim.
  const [bdCache, setBdCache] = useState({});

  // Fatura Geçmişi (filtrelenebilir) — filtreler URL state'inde yaşar (geri/refresh korunur)
  const [gecmisOpen, setGecmisOpen] = useState(false);
  const [gecmisFilteredData, setGecmisFilteredData] = useState([]);
  const [gecmisFilteredLoading, setGecmisFilteredLoading] = useState(false);
  const fgBasDefault = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const fgBitDefault = new Date().toISOString().split('T')[0];
  const [fgBas, setFgBas] = useUrlState('fg_bas', fgBasDefault);
  const [fgBit, setFgBit] = useUrlState('fg_bit', fgBitDefault);
  const [fgTed, setFgTed] = useUrlState('fg_ted', '');
  const [fgAra, setFgAra] = useUrlState('fg_ara', '', { debounceMs: 300 });

  // Şirketler arası satış
  const [sirketlerArasiModal, setSirketlerArasiModal] = useState(false); // eslint-disable-line no-unused-vars
  const [sirketlerArasiData, setSirketlerArasiData] = useState(null);
  const [karMarji, setKarMarji] = useState(20); // eslint-disable-line no-unused-vars
  const [sasSaving, setSasSaving] = useState(false); // eslint-disable-line no-unused-vars
  const [sasMessage, setSasMessage] = useState(null);

  const KATEGORI_OPTIONS = ['Hammadde', 'Yari Mamul', 'AMBALAJ', 'Kasap Ürünü', 'KARKAS', 'Diger'];

  const handleYeniMalzemeKaydet = async () => {
    if (!yeniMalzemeForm.ad.trim()) { toast.error('Malzeme adı zorunlu'); return; }
    setYeniMalzemeSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/malzemeler`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...yeniMalzemeForm,
          min_stok: parseFloat(yeniMalzemeForm.min_stok) || 0,
          marka_id: yeniMalzemeForm.marka_id ? parseInt(yeniMalzemeForm.marka_id, 10) : null,
        })
      });
      if (res.ok) {
        const created = await res.json();
        // Malzeme listesini güncelle
        const listRes = await fetch(`${API_URL}/api/malzemeler`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (listRes.ok) setMalzemeler(await listRes.json());
        setShowMalzemeModal(false);
        setYeniMalzemeForm({ kod: '', ad: '', kategori: 'Hammadde', birim: 'KG', min_stok: '0', marka_id: '' });
        invalidateQueries(['stok', 'dashboard']);
        toast.success(`"${created.ad}" malzemesi eklendi`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Malzeme eklenemedi');
      }
    } catch (e) {
      logError('handleYeniMalzemeKaydet', e);
      toast.error('Hata: ' + e.message);
    }
    setYeniMalzemeSaving(false);
  };

  const loadTedarikçiler = useCallback(() => {
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/api/tedarikciler`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setTedarikçiler(Array.isArray(data) ? data : []))
      .catch(() => setTedarikçiler([]));
  }, []);

  useEffect(() => {
    loadTedarikçiler();
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/api/malzemeler`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setMalzemeler(Array.isArray(data) ? data : []))
      .catch(() => setMalzemeler([]));
    fetch(`${API_URL}/api/lokasyonlar`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setLokasyonlar(Array.isArray(data) ? data : []))
      .catch(() => setLokasyonlar([]));
    // Markalar — "Yeni Malzeme Ekle" modalındaki dropdown için
    fetch(`${API_URL}/api/markalar`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setMarkalar(Array.isArray(data) ? data : []))
      .catch(() => setMarkalar([]));
    // Firmalar — fatura/fiyat firma seçimi (DB-driven, Anayasa M.1)
    fetch(`${API_URL}/api/firmalar`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setFirmalar(Array.isArray(data) ? data : []))
      .catch(() => setFirmalar([]));
  }, [loadTedarikçiler]);

  const handleTedarikciSaved = (newTedarikci) => {
    loadTedarikçiler();
    if (newTedarikci?.id) setSelectedTedarikci(String(newTedarikci.id));
    setShowTedarikciModal(false);
    toast.success(`"${newTedarikci?.unvan}" tedarikçi eklendi`);
  };

  const handleKalemFieldChange = useCallback((index, field, value) => {
    setKalemler(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleMalzemeSelect = useCallback((index, malzeme) => {
    setKalemler(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        malzeme_id: malzeme.id,
        birim: malzeme.birim || 'KG',
        birim_fiyat: updated[index].birim_fiyat || (malzeme.fiyat > 0 ? malzeme.fiyat : '')
      };
      return updated;
    });

    // Malzemenin BirimDonusum tanımlarını cache'le (dropdown seçenekleri + canlı
    // önizleme için). Birimi OTOMATİK alış birimine (KOLI/PAKET) ÇEVİRME — varsayılan
    // STOK birimi kalır. Geçmiş girişler kg cinsinden yapıldığı için otomatik KOLI
    // "ne yazarsan o kaydedilir" beklentisini bozup sessiz double-conversion riski
    // yaratıyordu (kullanıcı kg değerini KOLI alanına girip fark etmeyebilir).
    // Alış birimini kullanıcı bilerek seçer; satırdaki önizleme sonucu gösterir.
    if (!bdCache[malzeme.id]) {
      const token = localStorage.getItem('access_token');
      fetch(`${API_URL}/api/birim-donusumleri?malzeme_id=${malzeme.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : [])
        .then(bds => {
          if (Array.isArray(bds)) setBdCache(prev => ({ ...prev, [malzeme.id]: bds }));
        })
        .catch(() => {});
    }
  }, [bdCache]);

  // Kalem için kullanılabilir birim seçenekleri.
  // BD varsa: tüm alış birimleri + stok birimi (dönüşüm yok yolu için).
  // BD yoksa: malzeme.birim (fallback) — eski davranış.
  const getBirimOptions = useCallback((kalem) => {
    if (!kalem.malzeme_id) return BIRIM_OPTIONS;
    const bds = bdCache[kalem.malzeme_id];
    if (!Array.isArray(bds) || bds.length === 0) {
      const malzeme = malzemeler.find(m => m.id === parseInt(kalem.malzeme_id, 10));
      return malzeme?.birim ? [malzeme.birim] : BIRIM_OPTIONS;
    }
    const opts = new Set();
    bds.forEach(bd => {
      if (bd.stok_birimi) opts.add(bd.stok_birimi);
      if (bd.alis_birimi) opts.add(bd.alis_birimi);
    });
    return [...opts];
  }, [bdCache, malzemeler]);

  const handleSatirEkle = () => {
    setKalemler(prev => [...prev, { malzeme_id: '', miktar: '', birim_fiyat: '', birim: 'KG', kdv_orani: null }]);
  };

  const handleSatirSil = (index) => {
    if (kalemler.length <= 1) return;
    setKalemler(prev => prev.filter((_, i) => i !== index));
  };

  // Lokasyon adından firma koduna eşleme
  const getLokasyonFirma = (lokasyonId) => {
    const lok = lokasyonlar.find(l => String(l.id) === String(lokasyonId));
    if (!lok) return null;
    const ad = normalizeForSearch(lok.ad);
    if (ad.includes('inga')) return 'INGA_BURGER';
    if (ad.includes('alt') || ad.includes('üstü') || ad.includes('kofte') || ad.includes('köfte')) return 'ALTI_USTU_KOFTE';
    return 'TORUNA_KASAP';
  };

  // Girilen fiyat KDV dahilse nete cevir, degilse aynen net kabul et
  const getNetFiyat = (birimFiyat) => {
    const fiyat = parseTutarTR(birimFiyat) || 0;
    if (!kdvDahil || fiyat === 0) return fiyat;
    return fiyat / (1 + kdvOrani / 100);
  };

  // Kullanıcı tarafinda girilen tutar toplami (KDV dahil seciliyse brut, degilse net)
  const getGirilenToplam = () => {
    return kalemler.reduce((sum, k) => {
      const miktar = parseTutarTR(k.miktar) || 0;
      const fiyat = parseTutarTR(k.birim_fiyat) || 0;
      return sum + (miktar * fiyat);
    }, 0);
  };

  // KDV haric net toplam
  const getNetToplam = () => {
    return kalemler.reduce((sum, k) => {
      const miktar = parseTutarTR(k.miktar) || 0;
      const netFiyat = getNetFiyat(k.birim_fiyat);
      return sum + (miktar * netFiyat);
    }, 0);
  };

  // Per-kalem KDV (2026-05-19): her kalem kendi kdv_orani'sini kullanır (null ise global default)
  const getKalemKdvOrani = (kalem) => {
    return kalem.kdv_orani !== null && kalem.kdv_orani !== undefined ? kalem.kdv_orani : kdvOrani;
  };

  const getKdvToplam = () => {
    return kalemler.reduce((sum, k) => {
      const miktar = parseTutarTR(k.miktar) || 0;
      // getNetFiyat HAM string bekler (içeride parseTutarTR çağırır). Önceden
      // burada parseTutarTR(k.birim_fiyat) ile sayıya çevirip geçiyorduk →
      // çift-parse: 49.505 (sayı) String'e dönüp binlik-nokta heuristiğiyle
      // 49505 (×1000) oluyordu → KDV/Genel Toplam şişiyordu. Ham string geç.
      const satirNetToplam = miktar * getNetFiyat(k.birim_fiyat);
      const kdvTutar = satirNetToplam * (getKalemKdvOrani(k) / 100);
      return sum + kdvTutar;
    }, 0);
  };

  // KDV dahil genel toplam (net + kdv)
  const getGenelToplam = () => {
    return getNetToplam() + getKdvToplam();
  };

  const handleKaydet = async (onayla = false) => {
    if (!selectedTedarikci) {
      toast.error('Tedarikçi seçiniz');
      return;
    }
    if (!selectedLokasyon) {
      toast.error('Lokasyon seçiniz');
      return;
    }

    const validKalemler = kalemler.filter(k => k.malzeme_id && (parseTutarTR(k.miktar) || 0) > 0 && (parseTutarTR(k.birim_fiyat) || 0) > 0);
    if (validKalemler.length === 0) {
      toast.error('En az bir kalem giriniz');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/finance/fiyat-girisi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tedarikci_id: parseInt(selectedTedarikci),
          firma: selectedFirma || null,
          fiyat_tarihi: fiyatTarihi,
          aciklama: açıklama || null,
          lokasyon_id: parseInt(selectedLokasyon),
          kdv_orani: kdvOrani,
          onayla: onayla === true,
          kalemler: validKalemler.map(k => {
            const netFiyat = getNetFiyat(k.birim_fiyat);
            return {
              malzeme_id: parseInt(k.malzeme_id),
              miktar: parseTutarTR(k.miktar),
              birim_fiyat: parseFloat(netFiyat.toFixed(4)),
              toplam_fiyat: parseTutarTR(k.miktar) * parseFloat(netFiyat.toFixed(4)),
              // Alış birimi (BirimDonusum varsa stok birimine çevrilir).
              // Boş gönderirsek backend dönüşüm uygulamaz → "1 PAKET = 125 TL"
              // raw kayıt olur ve reçete maliyeti 100× şişer (bug 2026-05-19).
              giris_birimi: k.birim || null,
              // Per-kalem KDV: null ise backend global default uygular
              kdv_orani: k.kdv_orani !== null && k.kdv_orani !== undefined ? k.kdv_orani : null
            };
          })
        })
      });

      const data = await response.json();

      // Çapraz-kanal çift giriş tespiti: backend kaydetmeden onay isterse modal aç.
      if (response.ok && data.onay_gerekli) {
        setCiftGirisCakismalar(data.cakismalar || []);
        setLoading(false);
        return;
      }

      if (response.ok) {
        const netToplam = data.genel_toplam || 0;
        const kdvTutar = netToplam * (kdvOrani / 100);
        const brutToplam = netToplam + kdvTutar;
        toast.success(`${data.kalemler?.length || 0} kalem fiyat kaydedildi. Net: ${netToplam.toFixed(2)} TL | KDV (%${kdvOrani}): ${kdvTutar.toFixed(2)} TL | Genel: ${brutToplam.toFixed(2)} TL`);
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl', 'cari', 'fatura', 'tedarikci']);

        // Şirketler arası satış kontrolü
        const lokFirma = getLokasyonFirma(selectedLokasyon);
        if (selectedFirma && lokFirma && selectedFirma !== lokFirma && data.fatura_olusturuldu) {
          setSirketlerArasiData({
            fatura_no: data.fatura_no,
            kaynakFirma: selectedFirma,
            hedefFirma: lokFirma,
            genel_toplam: netToplam,
            kalemler: data.kalemler || [],
            kdv_orani: kdvOrani
          });
          setSasMessage(null);
        } else {
          setSirketlerArasiData(null);
        }

        setKalemler([{ malzeme_id: '', miktar: '', birim_fiyat: '', birim: 'KG', kdv_orani: null }]);
        setAçıklama('');
        setSelectedLokasyon('');
      } else {
        toast.error(data.detail || 'Hata oluştu');
      }
    } catch (err) {
      logError('handleKaydet', err);
      toast.error('Sunucu hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadGecmis = async () => {
    setGecmisLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/finance/fiyat-gecmisi?limit=1000&kaynak_tipi=MANUAL`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setGecmis(data.kayitlar || []);
    } catch (e) {
      logError('loadGecmis', e);
      setGecmis([]);
    } finally {
      setGecmisLoading(false);
    }
  };

  const toggleGecmis = () => {
    if (!showGecmis) loadGecmis();
    setShowGecmis(!showGecmis);
  };

  // "Son Fatura Girişleri"ni fatura bazında grupla. fatura_id'si olmayan eski
  // kayıtlar (legacy) tekil olarak ayrılır — bunların düzenleme bağlantısı yok.
  const gecmisGruplu = React.useMemo(() => {
    const map = new Map();
    const tekil = [];
    gecmis.forEach(g => {
      if (!g.fatura_id) { tekil.push(g); return; }
      const key = g.fatura_id;
      if (!map.has(key)) {
        map.set(key, {
          fatura_id: g.fatura_id, fatura_no: g.fatura_no,
          tarih: g.fiyat_tarihi, firma: g.firma, tedarikci_ad: g.tedarikci_ad,
          kalemler: [], net: 0,
        });
      }
      const grup = map.get(key);
      grup.kalemler.push(g);
      grup.net += (g.toplam || 0);
    });
    return { gruplar: Array.from(map.values()), tekil };
  }, [gecmis]);

  // Açılır/kapanır grup detayı için açık fatura_id seti.
  const [acikGruplar, setAcikGruplar] = useState({});
  const toggleGrup = (faturaId) => setAcikGruplar(p => ({ ...p, [faturaId]: !p[faturaId] }));

  const loadGecmisFiltered = async (ov = {}) => {
    // ov: filtre değerleri açıkça geçilebilir (state güncellemesi async olduğundan
    // "Filtreleri temizle" sıfırlanmış değerlerle hemen yeniden çekebilsin).
    const bas = ov.bas !== undefined ? ov.bas : fgBas;
    const bit = ov.bit !== undefined ? ov.bit : fgBit;
    const ted = ov.ted !== undefined ? ov.ted : fgTed;
    setGecmisFilteredLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      if (bas) params.append('baslangic_tarihi', bas);
      if (bit) params.append('bitis_tarihi', bit);
      if (ted) params.append('tedarikci_id', ted);
      params.append('limit', '200');
      const res = await fetch(`${API_URL}/api/finance/fiyat-gecmisi?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGecmisFilteredData(data.kayitlar || (Array.isArray(data) ? data : data.items || []));
      }
    } catch (e) { logError('loadGecmisFiltered', e); }
    setGecmisFilteredLoading(false);
  };

  const handleGecmisFiltreTemizle = () => {
    setFgBas(fgBasDefault);
    setFgBit(fgBitDefault);
    setFgTed('');
    setFgAra('');
    loadGecmisFiltered({ bas: fgBasDefault, bit: fgBitDefault, ted: '' });
  };

  useEffect(() => {
    if (gecmisOpen && gecmisFilteredData.length === 0) loadGecmisFiltered();
  }, [gecmisOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredGecmisData = gecmisFilteredData.filter(g => {
    if (!fgAra) return true;
    const q = normalizeForSearch(fgAra);
    return searchMatchPre(g.malzeme_ad, q) ||
           searchMatchPre(g.tedarikci_ad, q) ||
           searchMatchPre(g.aciklama || g.açıklama, q);
  });

  const handleSirketlerArasiSatis = async () => { // eslint-disable-line no-unused-vars
    if (!sirketlerArasiData) return;
    setSasSaving(true);
    setSasMessage(null);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/sirketler-arasi-satis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          fiyat_girisi_fatura_no: sirketlerArasiData.fatura_no,
          hedef_firma: sirketlerArasiData.hedefFirma,
          kar_marji_pct: karMarji,
          kdv_orani: sirketlerArasiData.kdv_orani || 10
        })
      });
      const result = await res.json();
      if (res.ok) {
        setSasMessage({ text: result.mesaj || 'Şirketler arası satış oluşturuldu', type: 'success' });
        setSirketlerArasiModal(false);
        setSirketlerArasiData(null);
        invalidateQueries(['finance', 'cari', 'fatura']);
      } else {
        setSasMessage({ text: result.detail || 'Hata oluştu', type: 'error' });
      }
    } catch (err) {
      setSasMessage({ text: 'Sunucu hatası: ' + err.message, type: 'error' });
    } finally {
      setSasSaving(false);
    }
  };

  const getFirmaLabel = (code) => {
    const f = firmalar.find(o => o.kod === code);
    return f ? f.ad : code;
  };

  // Band dışı rozeti — kaynak: fiyat_uyarilari join'i (g.band_disi / g.band_uyari).
  // Tooltip: girilen vs referans fiyat + sapma % + yön.
  const renderBandDisiBadge = (g) => {
    if (!g || !g.band_disi) return null;
    const u = g.band_uyari || {};
    const ref = u.referans_fiyat;
    const sapma = u.sapma_yuzde;
    const yon = (u.yon || '').toUpperCase();
    const yonLabel = yon === 'YUKSEK' ? 'yüksek' : (yon === 'DUSUK' ? 'düşük' : '');
    const tip = (ref != null && sapma != null)
      ? `Girilen ${(g.birim_fiyat || 0).toFixed(2)}₺, beklenen ~${Number(ref).toFixed(2)}₺ (%${Math.abs(Number(sapma)).toFixed(0)} ${yonLabel})`.trim()
      : 'Bu fiyat band dışı olarak işaretlendi';
    return (
      <span
        title={tip}
        style={{
          display: 'inline-block', background: '#f8d7da', color: '#721c24',
          padding: '2px 8px', borderRadius: radius.lg, fontSize: 11, fontWeight: 600,
          whiteSpace: 'nowrap', cursor: 'help'
        }}
      >⚠ band dışı</span>
    );
  };

  // Fatura bazlı düzenleme modalını aç: detay endpoint'ten kalemleri çek,
  // miktar/birim_fiyat ham string'e (virgül) çevir — entry form deseni.
  const openFaturaDuzenle = async (faturaId) => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/finance/fatura-girisi/${faturaId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Yüklenemedi'); return; }
      const d = await res.json();
      // Tam-hassasiyet prefill: backend stok-birim değerleri 4 ondalığa kadar saklanır
      // (BirimDonusum: birim_fiyat=round(.../katsayi,4), miktar=round(qty*katsayi,4)).
      // 2 ondalığa kırparsak dokunulmamış satır parseTutarTR ile round-trip yapamaz →
      // backend _stock_changed (>1e-9) sahte tetiklenir (gereksiz storno+yeniden) +
      // hassasiyet kaybı. String(n).replace('.',',') tam float'ı virgül-ondalık string'e
      // çevirir (JS sayı String'i binlik-nokta üretmez → parseTutarTR binlik-heuristiği
      // yanılmaz, round-trip birebir).
      const toEditStr = (n) => (n == null ? '' : String(n).replace('.', ','));
      setFaturaDuzenle({
        ...d,
        tedarikci_id: d.tedarikci_id != null ? String(d.tedarikci_id) : '',
        lokasyon_id: d.lokasyon_id != null ? String(d.lokasyon_id) : '',
        kalemler: (d.kalemler || []).map(k => ({
          id: k.id, malzeme_id: String(k.malzeme_id), malzeme_ad: k.malzeme_ad,
          malzeme_birim: k.malzeme_birim, miktar: toEditStr(k.miktar || 0),
          birim_fiyat: toEditStr(k.birim_fiyat || 0), kdv_orani: k.kdv_orani,
        })),
      });
    } catch (e) { logError('openFaturaDuzenle', e); toast.error('Hata: ' + e.message); }
  };

  // Modal kalem alanını immutable güncelle (index bazlı).
  const handleFaturaKalemChange = (index, field, value) => {
    setFaturaDuzenle(p => {
      if (!p) return p;
      const kalemler = p.kalemler.map((k, i) => i === index ? { ...k, [field]: value } : k);
      return { ...p, kalemler };
    });
  };

  const handleFaturaKalemMalzemeSelect = (index, malzeme) => {
    setFaturaDuzenle(p => {
      if (!p) return p;
      const kalemler = p.kalemler.map((k, i) => i === index
        ? { ...k, malzeme_id: String(malzeme.id), malzeme_ad: malzeme.ad, malzeme_birim: malzeme.birim || k.malzeme_birim }
        : k);
      return { ...p, kalemler };
    });
  };

  const handleFaturaKalemSil = (index) => {
    setFaturaDuzenle(p => p ? { ...p, kalemler: p.kalemler.filter((_, i) => i !== index) } : p);
  };

  const handleFaturaKalemEkle = () => {
    setFaturaDuzenle(p => p ? { ...p, kalemler: [...p.kalemler, { id: null, malzeme_id: '', miktar: '', birim_fiyat: '', kdv_orani: null }] } : p);
  };

  const handleFaturaDuzenleKaydet = async () => {
    if (!faturaDuzenle) return;
    const valid = faturaDuzenle.kalemler.filter(k => k.malzeme_id
      && (parseTutarTR(k.miktar) || 0) > 0 && (parseTutarTR(k.birim_fiyat) || 0) > 0);
    if (valid.length === 0) { toast.error('En az bir geçerli kalem girin'); return; }
    if (!faturaDuzenle.tedarikci_id) { toast.error('Tedarikçi seçin'); return; }
    if (!faturaDuzenle.lokasyon_id) { toast.error('Lokasyon seçin'); return; }
    setFaturaDuzenleSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/finance/fatura-girisi/${faturaDuzenle.fatura_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          tedarikci_id: parseInt(faturaDuzenle.tedarikci_id),
          firma: faturaDuzenle.firma || null,
          lokasyon_id: parseInt(faturaDuzenle.lokasyon_id),
          fiyat_tarihi: faturaDuzenle.fatura_tarihi,
          aciklama: faturaDuzenle.aciklama || null,
          kdv_orani: faturaDuzenle.kdv_orani != null ? Number(faturaDuzenle.kdv_orani) : null,
          // birim_fiyat NET stok-birim olarak gider — entry form'un getNetFiyat/BirimDonusum
          // dönüşümü UYGULANMAZ; GET'ten gelen değerler zaten net stok-birim.
          kalemler: valid.map(k => ({
            id: k.id || null,
            malzeme_id: parseInt(k.malzeme_id),
            miktar: parseTutarTR(k.miktar),
            birim_fiyat: parseTutarTR(k.birim_fiyat),
            kdv_orani: k.kdv_orani != null ? Number(k.kdv_orani) : null,
          })),
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const sas = data.sas_sonuc || {};
        toast.success(
          'Fatura güncellendi'
          + (sas.guncellenen > 0 ? ` · ${sas.guncellenen} şirketler-arası satış güncellendi` : '')
        );
        if (sas.korunan_kesilmis > 0) {
          toast.warning(
            `${sas.korunan_kesilmis} şirketler-arası satışın Logo faturası kesilmiş olduğu için korunmadı — Logo'da elle düzeltmen gerekebilir.`
          );
        }
        if (data.uyari) { toast.warning(data.uyari); }
        setFaturaDuzenle(null);
        loadGecmis();
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl', 'cari', 'fatura', 'tedarikci']);
      } else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Güncelleme hatası'); }
    } catch (e) { logError('handleFaturaDuzenleKaydet', e); toast.error('Hata: ' + e.message); }
    finally { setFaturaDuzenleSaving(false); }
  };

  // Fatura bazlı silme — mevcut DELETE /api/faturalar/{id} FG+stok+açık borcu geri alır.
  const handleFaturaSil = async (faturaId) => {
    if (!window.confirm('Bu faturanın TÜM kalemlerini ve ilişkili stok/cari kaydını silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/faturalar/${faturaId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.success(
          'Fatura silindi'
          + (data.sas_silindi > 0 ? ` · ${data.sas_silindi} şirketler-arası satış silindi` : '')
        );
        if (data.sas_korunan_kesilmis > 0) {
          toast.warning(
            `${data.sas_korunan_kesilmis} şirketler-arası satış Logo faturası kesilmiş olduğu için korundu (silinmedi) — gerekirse Logo'da elle düzelt.`
          );
        }
        loadGecmis();
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl', 'cari', 'fatura', 'tedarikci']);
      } else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Silme hatası'); }
    } catch (e) { logError('handleFaturaSil', e); toast.error('Hata: ' + e.message); }
  };

  return (
    <div>
      <Header title="Fatura Girişi" />
      <div style={{ padding: '20px' }}>

        {/* Şirketler Arası Satış Banner — 2 durum:
            1) Form doldurulurken: firma ≠ lokasyon → bilgi banner'ı
            2) Kaydet sonrası: sirketlerArasiData varsa → aksiyon butonlu banner */}
        {(() => {
          // Durum 2: Kaydet sonrası — form resetlense bile göster
          if (sirketlerArasiData) {
            return (
              <div style={{
                padding: '12px 16px', marginBottom: '15px', borderRadius: radius.md,
                background: '#fff3e0', border: '1px solid #ffcc02',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: '10px',
              }}>
                <span style={{ fontSize: '14px', color: '#e65100' }}>
                  <strong>{getFirmaLabel(sirketlerArasiData.kaynakFirma)}</strong> → <strong>{getFirmaLabel(sirketlerArasiData.hedefFirma)}</strong> şirketler arası satış oluşturabilirsiniz.
                  ({sirketlerArasiData.genel_toplam?.toLocaleString('tr-TR')} TL alış)
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => { setSirketlerArasiModal(true); setSasMessage(null); }}
                    style={{
                      background: '#f57c00', color: 'white', border: 'none', borderRadius: radius.md,
                      padding: '10px 24px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Şirketler Arası Satış Oluştur
                  </button>
                  <button
                    onClick={() => setSirketlerArasiData(null)}
                    style={{
                      background: colors.surface, color: colors.textTertiary, border: `1px solid ${colors.border}`, borderRadius: radius.md,
                      padding: '10px 16px', fontSize: '13px', cursor: 'pointer',
                    }}
                  >
                    Kapat
                  </button>
                </div>
              </div>
            );
          }
          // Durum 1: Form doldurulurken — bilgi banner'ı
          const lokFirma = getLokasyonFirma(selectedLokasyon);
          const firmaFarkli = selectedFirma && lokFirma && selectedFirma !== lokFirma;
          if (!firmaFarkli) return null;
          return (
            <div style={{
              padding: '12px 16px', marginBottom: '15px', borderRadius: radius.md,
              background: '#e3f2fd', border: '1px solid #90caf9',
            }}>
              <span style={{ fontSize: '14px', color: '#1565c0' }}>
                <strong>{getFirmaLabel(selectedFirma)}</strong> firmasına fatura, <strong>{getFirmaLabel(lokFirma)}</strong> lokasyonuna stok girişi yapılacak.
                Kaydet sonrası şirketler arası satış oluşturabilirsiniz.
              </span>
            </div>
          );
        })()}

        {/* SAS Mesaj */}
        {sasMessage && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '15px',
            borderRadius: radius.md,
            background: sasMessage.type === 'success' ? '#d4edda' : '#f8d7da',
            color: sasMessage.type === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${sasMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            {sasMessage.text}
          </div>
        )}

        {/* Form Karti */}
        <div style={{
          background: colors.surface,
          borderRadius: radius.lg,
          padding: '24px',
          boxShadow: shadows.md,
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 20px', color: styles.secondary }}>Fiş Bilgileri</h3>

          {/* Enter ile alan-gezme: alanlar arası ilerle, kalem birim fiyatında yeni satır, son alanda kaydet */}
          <EnterNavForm onSubmit={handleKaydet} onRowAdd={handleSatirEkle}>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            {/* Firma */}
            <div>
              <label style={labelStyle}>Firma *</label>
              <select
                value={selectedFirma}
                onChange={(e) => setSelectedFirma(e.target.value)}
                style={inputStyle}
              >
                <option value="">Firma Seçiniz</option>
                {firmalar.filter(f => f.aktif !== false).map(f => (
                  <option key={f.kod} value={f.kod}>{f.ad}{f.tip === 'MASRAF_MERKEZI' ? ' (Masraf Merkezi)' : ''}</option>
                ))}
              </select>
            </div>

            {/* Tedarikçi */}
            <div>
              <label style={labelStyle}>Tedarikçi *</label>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SearchableSelect
                    options={tedarikciler.map(t => ({ value: String(t.id), label: t.unvan }))}
                    value={selectedTedarikci}
                    onChange={setSelectedTedarikci}
                    placeholder="Tedarikçi ara…"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowTedarikciModal(true)}
                  title="Yeni tedarikçi ekle"
                  style={{
                    background: styles.primary, color: 'white', border: 'none',
                    borderRadius: radius.sm, padding: '0 12px', cursor: 'pointer',
                    fontSize: '18px', fontWeight: 'bold', flexShrink: 0
                  }}
                >+</button>
              </div>
            </div>

            {/* Lokasyon */}
            <div>
              <label style={labelStyle}>Lokasyon (Depo) *</label>
              <select
                value={selectedLokasyon}
                onChange={(e) => setSelectedLokasyon(e.target.value)}
                style={inputStyle}
              >
                <option value="">Lokasyon Seçiniz</option>
                {lokasyonlar.map(l => (
                  <option key={l.id} value={l.id}>{l.ad}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            {/* Tarih */}
            <div>
              <label style={labelStyle}>Fiyat Tarihi *</label>
              <input
                type="date"
                value={fiyatTarihi}
                onChange={(e) => setFiyatTarihi(e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Açıklama */}
            <div>
              <label style={labelStyle}>Fiş No / Açıklama</label>
              <input
                type="text"
                value={açıklama}
                onChange={(e) => setAçıklama(e.target.value)}
                placeholder="Fatura/Fiş numarası..."
                style={inputStyle}
              />
            </div>
          </div>

          {/* KDV Seçimi */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            padding: '12px 16px',
            background: kdvDahil ? '#fff8e1' : '#f8f9fa',
            borderRadius: radius.md,
            marginBottom: '20px',
            border: kdvDahil ? '1px solid #ffe082' : '1px solid #e9ecef'
          }}>
            <Toggle
              checked={kdvDahil}
              onChange={(e) => setKdvDahil(e.target.checked)}
              label="Girilen Fiyat KDV Dahil"
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: colors.textSecondary }}>KDV Oranı:</span>
              <select
                value={kdvOrani}
                onChange={(e) => setKdvOrani(parseFloat(e.target.value))}
                style={{
                  padding: '4px 8px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                <option value={1}>%1</option>
                <option value={10}>%10</option>
                <option value={20}>%20</option>
              </select>
              <span style={{ fontSize: '12px', color: kdvDahil ? '#e65100' : '#555', fontStyle: 'italic' }}>
                {kdvDahil
                  ? 'Brüt fiyat girişi: kayıtta nete çevrilir'
                  : 'Net fiyat girişi: KDV sistemde hesaplanır'}
              </span>
            </div>
          </div>

          {/* Kalemler Tablosu */}
          <h3 style={{ margin: '0 0 10px', color: styles.secondary }}>Kalemler</h3>
          {/* overflowX:auto — tablo min-content viewport'u aşınca sayfa yerine
              kart içinde yatay kaydırılır (yatay taşma fix). MalzemeAutocomplete
              dropdown'u portal ile body'ye render edildiği için kırpılmaz. */}
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt }}>
                <th style={{ ...thStyle, width: '35px' }}>#</th>
                <th style={thStyle}>Malzeme *</th>
                <th style={{ ...thStyle, width: '90px' }}>Miktar *</th>
                <th style={{ ...thStyle, width: '90px' }}>Birim</th>
                <th style={{ ...thStyle, width: '120px' }}>
                  Birim Fiyat *
                  {kdvDahil
                    ? <span style={{ fontSize: '10px', color: '#e65100', display: 'block' }}>(KDV dahil)</span>
                    : <span style={{ fontSize: '10px', color: '#555', display: 'block' }}>(KDV haric / Net)</span>}
                </th>
                <th style={{ ...thStyle, width: '85px' }}>
                  KDV %
                  <span style={{ fontSize: '10px', color: '#555', display: 'block', fontWeight: 'normal' }}>(per-kalem)</span>
                </th>
                {kdvDahil && <th style={{ ...thStyle, width: '100px' }}>Net Fiyat</th>}
                <th style={{ ...thStyle, width: '110px' }}>Toplam (TL)</th>
                <th style={{ ...thStyle, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {kalemler.map((kalem, index) => {
                const miktar = parseTutarTR(kalem.miktar) || 0;
                const fiyat = parseTutarTR(kalem.birim_fiyat) || 0;
                const toplam = miktar * fiyat;
                const netFiyat = getNetFiyat(kalem.birim_fiyat);

                return (
                  <tr key={index} data-enter-row style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={{ ...tdStyle, position: 'relative' }}>
                      <MalzemeAutocomplete
                        malzemeler={malzemeler}
                        value={kalem.malzeme_id}
                        onChange={(val) => handleKalemFieldChange(index, 'malzeme_id', val)}
                        onSelect={(m) => handleMalzemeSelect(index, m)}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={kalem.miktar}
                        onChange={(e) => handleKalemFieldChange(index, 'miktar', e.target.value)}
                        placeholder="0"
                        style={{ ...inputStyle, marginBottom: 0, textAlign: 'right' }}
                      />
                      {(() => {
                        // Canlı dönüşüm önizlemesi: birim ≠ stok birimi + aktif BD varsa,
                        // backend'in KAYDEDECEĞİ stok-birim miktar/fiyatı göster. Kullanıcı
                        // kg değerini KOLI alanına girerse saçma sonuç burada göze çarpar.
                        const malz = malzemeler.find(m => m.id === parseInt(kalem.malzeme_id, 10));
                        const onz = donusumOnizleme({
                          miktar: parseTutarTR(kalem.miktar) || 0,
                          birim: kalem.birim,
                          netFiyat: getNetFiyat(kalem.birim_fiyat),
                          malzemeBirim: malz?.birim,
                          bdList: bdCache[kalem.malzeme_id],
                        });
                        if (!onz) return null;
                        return (
                          <div
                            data-testid={`donusum-onizleme-${index}`}
                            title="Stok birimine çevrilmiş değer — kaydedilecek miktar/fiyat budur"
                            style={{ fontSize: '11px', marginTop: '4px', lineHeight: 1.3,
                                     color: '#92400e', fontWeight: 600, whiteSpace: 'normal' }}
                          >
                            ≈ {trSayi(parseTutarTR(kalem.miktar) || 0, 3)} {onz.alisBirim} × {trSayi(onz.katsayi, 3)} = <strong>{trSayi(onz.stokMiktar, 3)} {onz.stokBirim}</strong>
                            {onz.stokBirimFiyat != null && <> · {trSayi(onz.stokBirimFiyat, 2)} ₺/{onz.stokBirim}</>}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={tdStyle}>
                      <select
                        value={kalem.birim}
                        onChange={(e) => handleKalemFieldChange(index, 'birim', e.target.value)}
                        style={{ ...inputStyle, marginBottom: 0, textAlign: 'center' }}
                        title={
                          (bdCache[kalem.malzeme_id] || []).length > 0
                            ? 'Alış birimi — Birim Dönüşüm tanımlarından geliyor'
                            : 'Birim Dönüşüm tanımı yok'
                        }
                      >
                        {getBirimOptions(kalem).map(b => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input
                        type="text"
                        inputMode="decimal"
                        data-enter-nav="row-add"
                        value={kalem.birim_fiyat}
                        onChange={(e) => handleKalemFieldChange(index, 'birim_fiyat', e.target.value)}
                        placeholder="0,00"
                        style={{ ...inputStyle, marginBottom: 0, textAlign: 'right' }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <select
                        value={getKalemKdvOrani(kalem)}
                        onChange={(e) => handleKalemFieldChange(index, 'kdv_orani', parseFloat(e.target.value))}
                        style={{ ...inputStyle, marginBottom: 0, textAlign: 'center', fontWeight: 'bold' }}
                        title={kalem.kdv_orani === null || kalem.kdv_orani === undefined
                          ? `Global default: %${kdvOrani}`
                          : `Kalem KDV: %${kalem.kdv_orani}`}
                      >
                        <option value={1}>%1</option>
                        <option value={10}>%10</option>
                        <option value={20}>%20</option>
                      </select>
                    </td>
                    {kdvDahil && (
                      <td style={{ ...tdStyle, textAlign: 'right', color: colors.textSecondary, fontSize: '13px' }}>
                        {netFiyat > 0 ? `${netFiyat.toFixed(2)}` : '-'}
                      </td>
                    )}
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: styles.primary }}>
                      {toplam > 0 ? `${toplam.toFixed(2)}` : '-'}
                    </td>
                    <td style={tdStyle}>
                      {kalemler.length > 1 && (
                        <button
                          onClick={() => handleSatirSil(index)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: radius.sm,
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          X
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Satir Ekle + Toplam */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSatirEkle}
                style={{
                  background: colors.surface,
                  color: styles.primary,
                  border: `2px dashed ${styles.primary}`,
                  borderRadius: radius.md,
                  padding: '8px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                + Satır Ekle
              </button>
              <button
                onClick={() => setShowMalzemeModal(true)}
                style={{
                  background: colors.surface,
                  color: '#28a745',
                  border: '2px dashed #28a745',
                  borderRadius: radius.md,
                  padding: '8px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                + Yeni Malzeme
              </button>
            </div>

            <div style={{
              background: colors.backgroundAlt,
              padding: '12px 24px',
              borderRadius: radius.md,
              textAlign: 'right'
            }}>
              <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' }}>
                Girilen Toplam: <span style={{ fontWeight: 'bold' }}>{getGirilenToplam().toFixed(2)} TL</span>
              </div>
              <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' }}>
                Net Toplam: <span style={{ fontWeight: 'bold' }}>{getNetToplam().toFixed(2)} TL</span>
              </div>
              <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' }}>
                KDV Tutarı (%{kdvOrani}): <span style={{ fontWeight: 'bold' }}>{getKdvToplam().toFixed(2)} TL</span>
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: styles.secondary }}>
                Genel Toplam (KDV Dahil): <span style={{ color: styles.primary }}>{getGenelToplam().toFixed(2)} TL</span>
              </div>
            </div>
          </div>

          {/* Kaydet */}
          <div style={{ textAlign: 'right', marginTop: '20px' }}>
            <button
              onClick={() => handleKaydet()}
              disabled={loading}
              style={{
                background: loading ? '#ccc' : styles.primary,
                color: 'white',
                border: 'none',
                borderRadius: radius.md,
                padding: '12px 40px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loading ? 'default' : 'pointer'
              }}
            >
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>

          </EnterNavForm>
        </div>

        {/* Gecmis Kayıtlar */}
        <div style={{
          background: colors.surface,
          borderRadius: radius.lg,
          padding: '24px',
          boxShadow: shadows.md
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: styles.secondary }}>Son Fatura Girişleri</h3>
            <button
              onClick={toggleGecmis}
              style={{
                background: showGecmis ? '#6c757d' : styles.info,
                color: 'white',
                border: 'none',
                borderRadius: radius.sm,
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              {showGecmis ? 'Gizle' : 'Göster'}
            </button>
          </div>

          {showGecmis && (
            gecmisLoading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary }}>Yükleniyor...</div>
            ) : gecmis.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: colors.textTertiary }}>Henüz fiyat girişi yapılmamış</div>
            ) : (
              <>
                {/* Fatura bazlı gruplar — her grup açılabilir kalem detayı + Düzenle/Sil */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {gecmisGruplu.gruplar.map(grup => {
                    const acik = !!acikGruplar[grup.fatura_id];
                    return (
                      <div key={grup.fatura_id} style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: '12px', padding: '10px 14px', background: colors.backgroundAlt, flexWrap: 'wrap',
                        }}>
                          <button
                            type="button"
                            onClick={() => toggleGrup(grup.fatura_id)}
                            style={{
                              flex: 1, minWidth: '240px', display: 'flex', alignItems: 'center', gap: '10px',
                              background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                              fontSize: '13px', color: colors.textPrimary, padding: 0,
                            }}
                          >
                            <span style={{ color: colors.textTertiary, width: '14px', flexShrink: 0 }}>{acik ? '▾' : '▸'}</span>
                            <span style={{ fontWeight: 600 }}>{formatTarihGoster(grup.tarih)}</span>
                            <span style={{ color: colors.textSecondary }}>· {grup.tedarikci_ad || '-'}</span>
                            <span style={{ color: colors.textSecondary }}>· {getFirmaLabel(grup.firma) || grup.firma || '-'}</span>
                            <span style={{ color: colors.textTertiary }}>· {grup.kalemler.length} kalem</span>
                            <span style={{ fontWeight: 700, color: styles.primary }}>· {grup.net.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL net</span>
                          </button>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <button
                              onClick={() => openFaturaDuzenle(grup.fatura_id)}
                              title="Düzenle"
                              style={{
                                background: '#ffc107', color: '#333', border: 'none',
                                borderRadius: radius.sm, padding: '4px 12px', cursor: 'pointer',
                                fontSize: '12px', fontWeight: 'bold',
                              }}
                            >Düzenle</button>
                            <button
                              onClick={() => handleFaturaSil(grup.fatura_id)}
                              title="Sil"
                              style={{
                                background: '#dc3545', color: 'white', border: 'none',
                                borderRadius: radius.sm, padding: '4px 12px', cursor: 'pointer',
                                fontSize: '12px', fontWeight: 'bold',
                              }}
                            >Sil</button>
                          </div>
                        </div>
                        {acik && (
                          <div style={{ overflowX: 'auto', padding: '4px 0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: colors.surface }}>
                                  <th style={thStyle}>Malzeme</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Miktar</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Birim Fiyat</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Toplam</th>
                                  <th style={thStyle}>Açıklama</th>
                                </tr>
                              </thead>
                              <tbody>
                                {grup.kalemler.map(g => (
                                  <tr key={g.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                                    <td style={tdStyle}>{g.malzeme_ad}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{g.miktar} {g.malzeme_birim}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                      {g.band_disi && <span style={{ marginRight: 6 }}>{renderBandDisiBadge(g)}</span>}
                                      {(g.birim_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>{(g.toplam || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                                    <td style={{ ...tdStyle, color: colors.textSecondary, fontSize: '12px' }}>{g.aciklama || g.açıklama || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Eski kayıtlar (legacy) — fatura bağlantısı yok, salt-okunur */}
                {gecmisGruplu.tekil.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: colors.textTertiary, marginBottom: '6px' }}>
                      Eski kayıtlar ({gecmisGruplu.tekil.length})
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {gecmisGruplu.tekil.map(g => (
                          <tr key={g.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                            <td style={{ ...tdStyle, fontSize: '13px' }}>{formatTarihGoster(g.fiyat_tarihi)}</td>
                            <td style={{ ...tdStyle, fontSize: '13px' }}>{g.malzeme_ad}</td>
                            <td style={{ ...tdStyle, fontSize: '13px', color: colors.textSecondary }}>{g.tedarikci_ad || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontSize: '13px' }}>{g.miktar} {g.malzeme_birim}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{(g.birim_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(g.toplam || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )
          )}
        </div>

        {/* Fatura Geçmişi — Filtrelenebilir */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'hidden', marginTop: '24px' }}>
          <button
            onClick={() => setGecmisOpen(v => !v)}
            style={{
              width: '100%', padding: '16px 20px', border: 'none', background: 'transparent',
              cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: '15px', fontWeight: 700, color: colors.textPrimary, textAlign: 'left',
            }}>
            <span>Fatura Geçmişi {gecmisFilteredData.length > 0 && <span style={{ fontSize: '12px', color: colors.textTertiary, fontWeight: 400 }}>({filteredGecmisData.length} kayıt)</span>}</span>
            <span style={{ fontSize: '12px', color: colors.textTertiary }}>{gecmisOpen ? 'Kapat' : 'Aç'}</span>
          </button>

          {gecmisOpen && (
            <div style={{ padding: '0 20px 20px' }}>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Başlangıç</label>
                  <input type="date" value={fgBas}
                    onChange={e => setFgBas(e.target.value)}
                    style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Bitiş</label>
                  <input type="date" value={fgBit}
                    onChange={e => setFgBit(e.target.value)}
                    style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }} />
                </div>
                <div style={{ minWidth: '220px' }}>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Tedarikçi</label>
                  <SearchableSelect
                    options={tedarikciler.map(t => ({ value: String(t.id), label: t.unvan }))}
                    value={fgTed}
                    onChange={setFgTed}
                    placeholder="Tedarikçi (tümü)…"
                  />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Malzeme / Tedarikçi Ara</label>
                  <input type="text" value={fgAra} placeholder="Ara..."
                    onChange={e => setFgAra(e.target.value)}
                    style={{ width: '100%', padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
                <button onClick={() => loadGecmisFiltered()} disabled={gecmisFilteredLoading}
                  style={{
                    padding: '8px 20px', background: gecmisFilteredLoading ? '#ccc' : colors.primary, color: 'white',
                    border: 'none', borderRadius: radius.sm, cursor: gecmisFilteredLoading ? 'default' : 'pointer',
                    fontWeight: 600, fontSize: '13px',
                  }}>
                  {gecmisFilteredLoading ? 'Yükleniyor...' : 'Getir'}
                </button>
                <button onClick={handleGecmisFiltreTemizle} type="button"
                  style={{
                    padding: '8px 16px', background: colors.surface, color: colors.textSecondary,
                    border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer',
                    fontWeight: 600, fontSize: '13px',
                  }}>
                  Filtreleri temizle
                </button>
              </div>

              {/* Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt }}>
                      {['Tarih', 'Fiş No', 'Tedarikçi', 'Malzeme', 'Miktar', 'Birim Fiyat', 'Toplam', 'Firma'].map(h => (
                        <th key={h} style={{
                          padding: '10px 14px', fontSize: '11px', fontWeight: 600,
                          color: colors.textTertiary, letterSpacing: '0.04em',
                          textAlign: h === 'Miktar' || h === 'Birim Fiyat' || h === 'Toplam' ? 'right' : 'left',
                          whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gecmisFilteredLoading ? (
                      Array.from({length: 5}).map((_, i) => (
                        <tr key={i}><td colSpan={7} style={{ padding: '10px 14px' }}>
                          <div style={{ height: 14, background: colors.border, borderRadius: 4, width: `${60+i*5}%` }} />
                        </td></tr>
                      ))
                    ) : filteredGecmisData.length === 0 ? (
                      <tr><td colSpan={8} style={{ padding: '24px 14px', textAlign: 'center', color: colors.textTertiary }}>Kayıt bulunamadı</td></tr>
                    ) : filteredGecmisData.map((g, i) => (
                      <tr key={g.id || i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '10px 14px', fontSize: '13px' }}>{formatTarihGoster(g.fiyat_tarihi || g.created_at)}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: colors.textSecondary, fontFamily: 'monospace' }}>{g.aciklama || g.açıklama || '-'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 500 }}>{g.tedarikci_ad || '-'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px' }}>{g.malzeme_ad || '-'}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', textAlign: 'right' }}>{(g.miktar || 0).toLocaleString('tr-TR')} {g.malzeme_birim || ''}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', textAlign: 'right' }}>{(g.birim_fiyat || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', textAlign: 'right', fontWeight: 600 }}>{(g.toplam || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: colors.textSecondary }}>{getFirmaLabel(g.firma) || g.firma || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  {filteredGecmisData.length > 0 && (
                    <tfoot>
                      <tr style={{ background: colors.backgroundAlt, borderTop: `2px solid ${colors.border}` }}>
                        <td colSpan={6} style={{ padding: '10px 14px', fontWeight: 700, fontSize: '13px' }}>Toplam ({filteredGecmisData.length} kayıt)</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, fontSize: '14px' }}>
                          {filteredGecmisData.reduce((s, g) => s + (g.toplam || 0), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL
                        </td>
                        <td style={{ padding: '10px 14px' }}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fatura Düzenleme Modal — fatura bazlı (header + kalem tablosu) */}
      {faturaDuzenle && (() => {
        const netToplam = faturaDuzenle.kalemler.reduce((s, k) =>
          s + (parseTutarTR(k.miktar) || 0) * (parseTutarTR(k.birim_fiyat) || 0), 0);
        return (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999
        }} onClick={() => setFaturaDuzenle(null)}>
          <div style={{
            background: colors.surface, borderRadius: radius.lg, padding: '24px',
            width: '760px', maxWidth: '94vw', boxSizing: 'border-box',
            maxHeight: '90vh', overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: styles.secondary }}>
                Fatura Düzenle {faturaDuzenle.fatura_no && <span style={{ fontSize: '13px', color: colors.textTertiary, fontWeight: 400 }}>· {faturaDuzenle.fatura_no}</span>}
              </h3>
              <button onClick={() => setFaturaDuzenle(null)} style={{
                background: 'none', border: 'none', fontSize: '20px',
                cursor: 'pointer', color: colors.textTertiary, padding: '4px'
              }}>X</button>
            </div>

            {faturaDuzenle.odeme_var && (
              <div style={{ padding: '10px 14px', marginBottom: '10px', borderRadius: radius.sm, background: '#fff8e1', border: '1px solid #ffe082', fontSize: '13px', color: '#e65100' }}>
                ⚠ Bu faturaya ödeme/tahsilat işlenmiş; tutar değişimi cari bakiyeyi etkiler.
              </div>
            )}
            {faturaDuzenle.sas_var && (
              <div style={{ padding: '10px 14px', marginBottom: '10px', borderRadius: radius.sm, background: '#fff8e1', border: '1px solid #ffe082', fontSize: '13px', color: '#e65100' }}>
                ⚠ Bu faturadan şirketler-arası satış üretilmiş; kaydedince kesilmemişler otomatik güncellenecek, Logo faturası kesilmiş olanlar korunacak (gerekirse Logo'da elle düzelt).
              </div>
            )}

            {/* Header alanları */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={modalLabelStyle}>Tedarikçi *</label>
                <SearchableSelect
                  options={tedarikciler.map(t => ({ value: String(t.id), label: t.unvan }))}
                  value={faturaDuzenle.tedarikci_id}
                  onChange={(val) => setFaturaDuzenle(p => ({ ...p, tedarikci_id: val }))}
                  placeholder="Tedarikçi ara…"
                />
              </div>
              <div>
                <label style={modalLabelStyle}>Lokasyon (Depo) *</label>
                <select value={faturaDuzenle.lokasyon_id}
                  onChange={e => setFaturaDuzenle(p => ({ ...p, lokasyon_id: e.target.value }))}
                  style={modalInputStyle}>
                  <option value="">Lokasyon Seçiniz</option>
                  {lokasyonlar.map(l => (
                    <option key={l.id} value={String(l.id)}>{l.ad}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={modalLabelStyle}>Firma</label>
                <select value={faturaDuzenle.firma || ''}
                  onChange={e => setFaturaDuzenle(p => ({ ...p, firma: e.target.value }))}
                  style={modalInputStyle}>
                  <option value="">-</option>
                  {firmalar.filter(f => f.aktif !== false).map(f => (
                    <option key={f.kod} value={f.kod}>{f.ad}{f.tip === 'MASRAF_MERKEZI' ? ' (Masraf Merkezi)' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={modalLabelStyle}>Tarih</label>
                <input type="date" value={faturaDuzenle.fatura_tarihi || ''}
                  onChange={e => setFaturaDuzenle(p => ({ ...p, fatura_tarihi: e.target.value }))}
                  style={modalInputStyle} />
              </div>
              <div>
                <label style={modalLabelStyle}>Fiş No / Açıklama</label>
                <input type="text" value={faturaDuzenle.aciklama || ''}
                  onChange={e => setFaturaDuzenle(p => ({ ...p, aciklama: e.target.value }))}
                  placeholder="Fatura/Fiş numarası..." style={modalInputStyle} />
              </div>
              <div>
                <label style={modalLabelStyle}>Genel KDV %</label>
                <input type="number" min="0" step="1"
                  value={faturaDuzenle.kdv_orani != null ? faturaDuzenle.kdv_orani : ''}
                  onChange={e => setFaturaDuzenle(p => ({ ...p, kdv_orani: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                  style={modalInputStyle} />
              </div>
            </div>

            {/* Kalemler */}
            <label style={modalLabelStyle}>Kalemler</label>
            <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt }}>
                    <th style={thStyle}>Malzeme *</th>
                    <th style={{ ...thStyle, width: '100px' }}>Miktar *</th>
                    <th style={{ ...thStyle, width: '120px' }}>Birim Fiyat *<span style={{ fontSize: '10px', color: '#555', display: 'block', fontWeight: 'normal' }}>(net stok-birim)</span></th>
                    <th style={{ ...thStyle, width: '85px' }}>KDV %</th>
                    <th style={{ ...thStyle, width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {faturaDuzenle.kalemler.map((k, index) => (
                    <tr key={k.id ?? `new-${index}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ ...tdStyle, position: 'relative', minWidth: '220px' }}>
                        <MalzemeAutocomplete
                          malzemeler={malzemeler}
                          value={k.malzeme_id}
                          onChange={(val) => handleFaturaKalemChange(index, 'malzeme_id', val)}
                          onSelect={(m) => handleFaturaKalemMalzemeSelect(index, m)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input type="text" inputMode="decimal" value={k.miktar}
                          onChange={e => handleFaturaKalemChange(index, 'miktar', e.target.value)}
                          placeholder="0" style={{ ...inputStyle, marginBottom: 0, textAlign: 'right' }} />
                      </td>
                      <td style={tdStyle}>
                        <input type="text" inputMode="decimal" value={k.birim_fiyat}
                          onChange={e => handleFaturaKalemChange(index, 'birim_fiyat', e.target.value)}
                          placeholder="0,00" style={{ ...inputStyle, marginBottom: 0, textAlign: 'right' }} />
                      </td>
                      <td style={tdStyle}>
                        <select
                          value={k.kdv_orani != null ? k.kdv_orani : (faturaDuzenle.kdv_orani != null ? faturaDuzenle.kdv_orani : 10)}
                          onChange={e => handleFaturaKalemChange(index, 'kdv_orani', parseFloat(e.target.value))}
                          style={{ ...inputStyle, marginBottom: 0, textAlign: 'center', fontWeight: 'bold' }}>
                          <option value={1}>%1</option>
                          <option value={10}>%10</option>
                          <option value={20}>%20</option>
                        </select>
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => handleFaturaKalemSil(index)}
                          style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '14px' }}>X</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <button onClick={handleFaturaKalemEkle} style={{
                background: colors.surface, color: styles.primary, border: `2px dashed ${styles.primary}`,
                borderRadius: radius.md, padding: '8px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold'
              }}>+ Kalem ekle</button>
              <div style={{ background: colors.backgroundAlt, padding: '10px 20px', borderRadius: radius.md, textAlign: 'right', fontSize: '15px' }}>
                Net Toplam: <strong style={{ color: styles.primary }}>{netToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</strong>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setFaturaDuzenle(null)} style={{
                background: colors.backgroundAlt, color: '#333', border: `1px solid ${colors.border}`,
                borderRadius: radius.sm, padding: '8px 20px', cursor: 'pointer', fontSize: '14px'
              }}>İptal</button>
              <button onClick={handleFaturaDuzenleKaydet} disabled={faturaDuzenleSaving} style={{
                background: faturaDuzenleSaving ? '#ccc' : styles.primary, color: 'white',
                border: 'none', borderRadius: radius.sm, padding: '8px 24px',
                cursor: faturaDuzenleSaving ? 'default' : 'pointer', fontSize: '14px', fontWeight: 'bold'
              }}>{faturaDuzenleSaving ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Şirketler Arası Satış Modal */}
      {sirketlerArasiModal && sirketlerArasiData && (() => {
        const alisT = sirketlerArasiData.genel_toplam || 0;
        const satisT = alisT * (1 + karMarji / 100);
        const karT = satisT - alisT;
        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999
          }} onClick={() => setSirketlerArasiModal(false)}>
            <div style={{
              background: colors.surface, borderRadius: radius.lg, padding: '24px',
              width: '600px', maxWidth: '92vw', boxSizing: 'border-box',
              maxHeight: '90vh', overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#e65100' }}>Şirketler Arası Satış</h3>
                <button onClick={() => setSirketlerArasiModal(false)} style={{
                  background: 'none', border: 'none', fontSize: '20px',
                  cursor: 'pointer', color: colors.textTertiary, padding: '4px'
                }}>X</button>
              </div>

              {sasMessage && (
                <div style={{
                  padding: '8px 12px', marginBottom: '12px', borderRadius: radius.sm,
                  background: sasMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                  color: sasMessage.type === 'success' ? '#155724' : '#721c24',
                  fontSize: '13px'
                }}>{sasMessage.text}</div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={modalLabelStyle}>Kaynak Firma</label>
                  <input value={getFirmaLabel(sirketlerArasiData.kaynakFirma)} readOnly style={{ ...modalInputStyle, background: colors.backgroundAlt }} />
                </div>
                <div>
                  <label style={modalLabelStyle}>Hedef Firma</label>
                  <input value={getFirmaLabel(sirketlerArasiData.hedefFirma)} readOnly style={{ ...modalInputStyle, background: colors.backgroundAlt }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={modalLabelStyle}>Alış Toplamı (Net)</label>
                  <input value={`${alisT.toFixed(2)} TL`} readOnly style={{ ...modalInputStyle, background: colors.backgroundAlt, fontWeight: 'bold' }} />
                </div>
                <div>
                  <label style={modalLabelStyle}>Kâr Marjı %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={karMarji}
                    onChange={e => setKarMarji(parseFloat(e.target.value) || 0)}
                    style={{ ...modalInputStyle, fontWeight: 'bold', color: '#e65100' }}
                  />
                </div>
                <div>
                  <label style={modalLabelStyle}>Satış Toplamı</label>
                  <input value={`${satisT.toFixed(2)} TL`} readOnly style={{ ...modalInputStyle, background: '#e8f5e9', fontWeight: 'bold', color: '#2e7d32' }} />
                </div>
              </div>

              <div style={{
                padding: '10px 16px', background: '#fff3e0', borderRadius: radius.sm,
                marginBottom: '16px', textAlign: 'center', fontSize: '15px'
              }}>
                Kâr Tutarı: <strong style={{ color: '#e65100' }}>{karT.toFixed(2)} TL</strong>
              </div>

              {/* Kalemler */}
              {sirketlerArasiData.kalemler && sirketlerArasiData.kalemler.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={modalLabelStyle}>Kalemler</label>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: colors.backgroundAlt }}>
                        <th style={{ ...thStyle, fontSize: '12px' }}>Malzeme</th>
                        <th style={{ ...thStyle, fontSize: '12px', textAlign: 'right' }}>Miktar</th>
                        <th style={{ ...thStyle, fontSize: '12px', textAlign: 'right' }}>Alış Fiyat</th>
                        <th style={{ ...thStyle, fontSize: '12px', textAlign: 'right' }}>Satış Fiyat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sirketlerArasiData.kalemler.map((k, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={tdStyle}>{k.malzeme_ad}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{k.miktar}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{(k.birim_fiyat || 0).toFixed(2)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#2e7d32', fontWeight: 'bold' }}>
                            {((k.birim_fiyat || 0) * (1 + karMarji / 100)).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setSirketlerArasiModal(false)} style={{
                  background: colors.backgroundAlt, color: '#333', border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, padding: '8px 20px', cursor: 'pointer', fontSize: '14px'
                }}>İptal</button>
                <button onClick={handleSirketlerArasiSatis} disabled={sasSaving} style={{
                  background: sasSaving ? '#ccc' : '#f57c00', color: 'white',
                  border: 'none', borderRadius: radius.sm, padding: '8px 24px',
                  cursor: sasSaving ? 'default' : 'pointer', fontSize: '14px', fontWeight: 'bold'
                }}>{sasSaving ? 'Oluşturuluyor...' : 'Oluştur'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showTedarikciModal && (
        <TedarikçiEkleModal
          onClose={() => setShowTedarikciModal(false)}
          onSaved={handleTedarikciSaved}
        />
      )}

      {/* Yeni Malzeme Modal */}
      {showMalzemeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '24px', width: '450px', maxWidth: '92vw', boxSizing: 'border-box', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', color: styles.secondary }}>Yeni Malzeme Ekle</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Malzeme Kodu</label>
                <input value={yeniMalzemeForm.kod} onChange={e => setYeniMalzemeForm(p => ({...p, kod: e.target.value}))} placeholder="Otomatik atanır (boş bırakabilirsiniz)" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Malzeme Adı *</label>
                <input value={yeniMalzemeForm.ad} onChange={e => setYeniMalzemeForm(p => ({...p, ad: e.target.value}))} placeholder="Örn: Dana Kuşbaşı" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Kategori</label>
                <select value={yeniMalzemeForm.kategori} onChange={e => setYeniMalzemeForm(p => ({...p, kategori: e.target.value}))} style={inputStyle}>
                  {KATEGORI_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={labelStyle}>Birim</label>
                  <select value={yeniMalzemeForm.birim} onChange={e => setYeniMalzemeForm(p => ({...p, birim: e.target.value}))} style={inputStyle}>
                    {BIRIM_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Min Stok</label>
                  <input type="number" value={yeniMalzemeForm.min_stok} onChange={e => setYeniMalzemeForm(p => ({...p, min_stok: e.target.value}))} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle} title="Boş bırakırsanız tüm şubelerde görünür (paylaşılan / hammadde gibi). Bir marka seçerseniz sadece o markanın şubelerinde sayım listesinde çıkar.">
                  Marka <span style={{ color: '#888', fontWeight: 'normal' }}>(opsiyonel — boş = paylaşılan)</span>
                </label>
                <select value={yeniMalzemeForm.marka_id} onChange={e => setYeniMalzemeForm(p => ({...p, marka_id: e.target.value}))} style={inputStyle}>
                  <option value="">— Paylaşılan (tüm şubeler)</option>
                  {markalar.filter(m => m.aktif).map(m => (
                    <option key={m.id} value={m.id}>{m.ad}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setShowMalzemeModal(false)} style={{ padding: '8px 20px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, cursor: 'pointer' }}>İptal</button>
              <button onClick={handleYeniMalzemeKaydet} disabled={yeniMalzemeSaving} style={{ padding: '8px 20px', border: 'none', borderRadius: radius.sm, background: styles.primary, color: 'white', cursor: 'pointer', fontWeight: 'bold', opacity: yeniMalzemeSaving ? 0.6 : 1 }}>
                {yeniMalzemeSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Çapraz-kanal çift giriş onay modal'ı */}
      {ciftGirisCakismalar && (
        <div
          onClick={() => setCiftGirisCakismalar(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: colors.surface, borderRadius: radius.lg, padding: '24px', width: '460px', maxWidth: '92vw', boxSizing: 'border-box', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          >
            <h3 style={{ margin: '0 0 12px', color: '#e65100' }}>Olası çift giriş</h3>
            <p style={{ fontSize: '14px', color: colors.textSecondary, marginTop: 0 }}>
              Bu kalemler kısa süre içinde mal kabul (GRN) ile de girilmiş olabilir — çift stok riski:
            </p>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '13px', background: colors.backgroundAlt, padding: '8px', borderRadius: radius.sm, margin: '0 0 4px' }}>
              {ciftGirisOnayMetni(ciftGirisCakismalar)}
            </pre>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" onClick={() => setCiftGirisCakismalar(null)} style={{ padding: '8px 20px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, cursor: 'pointer' }}>Vazgeç</button>
              <button type="button" onClick={() => { setCiftGirisCakismalar(null); handleKaydet(true); }} style={{ padding: '8px 20px', border: 'none', borderRadius: radius.sm, background: styles.primary, color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>Yine de kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stiller
const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#555'
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '14px',
  boxSizing: 'border-box'
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#555',
  borderBottom: '2px solid #dee2e6'
};

const tdStyle = {
  padding: '8px 12px',
  fontSize: '14px',
  verticalAlign: 'middle'
};

export default FiyatGirisiPage;
